import React from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';
import ModalManager, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as userResource from 'resources/user/hooks';
import { getFullName } from 'resources/user/user.helpers';
import Modal from 'UI/Modal';
import Button from 'UI/Button';
import Input from 'UI/Input';
import styles from './styles.module.scss';

const generateValidators = (clients) => {
  const result = {};

  clients.forEach((client) => {
    result[client.id] = yup.string()
      .min(14, 'Incorrect phone number')
      .max(14, 'Incorrect phone number');
  });

  return result;
};


const EnterPhoneNumberModal = ModalManager.create(({ clientsWithoutNumber, onSubmit }) => {
  const modal = useModal();
  const queryClient = useQueryClient();
  const { mutateAsync: updateUser, isLoading: isUserUpdating } = userResource.useUserUpdate();

  const {
    handleSubmit, formState: { errors }, control,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(yup.object({ ...generateValidators(clientsWithoutNumber || []) })),
    shouldUnregister: true,
  });

  const onSavePhoneNumber = async (data) => {
    await Promise.all(
      Object.entries(data).map(
        ([clientId, phoneNumber]) => updateUser({ id: clientId, phone_number: phoneNumber }),
      ),
    );

    await queryClient.invalidateQueries([['users.getUsersByIds', Object.keys(data)]]);

    modal.hide();

    onSubmit?.();
  };

  return (
    <Modal
      title="Mobile number needed"
      subtitle="Invited clients will be able to view tasks, progress, and documents for their transactions.
      Not ready to share? You can invite a client any time."
      isOpen={modal.visible}
      onClose={modal.hide}
      onAfterClose={modal.remove}
      className={styles.modal}
    >
      <form onSubmit={handleSubmit(onSavePhoneNumber)}>
        {clientsWithoutNumber.map((client) => (
          <Input
            key={client.id}
            name={client.id}
            label={getFullName(client)}
            placeholder="Enter Phone Number"
            control={control}
            type="phoneNumber"
            error={errors[client.id]}
          />
        ))}

        <div className={styles.enterPhoneButtonsContainer}>
          <Button
            type="minimal"
            onClick={modal.hide}
            loading={isUserUpdating}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={isUserUpdating}
          >
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
});

EnterPhoneNumberModal.propTypes = {
  clientsWithoutNumber: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  })).isRequired,
};

export default React.memo(EnterPhoneNumberModal);
